import type { Content, Damage, RoomDef, RoomModifierDef, Slot, Stats } from '../data/schema';
import { buy, grantItemOfSlot, openShop, rerollShop, type ShopOffer, type ShopState } from './equipment';
import { clampBonuses, mergeBonuses, ZERO_META_BONUSES, type MetaBonuses } from './meta/bonuses';
import { scoreOf } from './meta/score';
import { createStatsTracker, type RunStats } from './meta/stats';
import { activeModifiers, applyClearReward, applyEntryReward, chooseDoor, isBossRoom, offerDoors, roomAssets as floorRoomAssets, roomSetup, ROOMS_TOTAL, startFloor, type DoorOffer, type FloorState } from './floor';
import { applyCard, drawCards, effectiveCooldown, type LevelUpCard } from './progression';
import { doorAt, placeDoors } from './map/doors';
import { playerDamagePct } from './combat';
import { createRng } from './rng';
import { animSystem } from './systems/anim';
import { monsterSystem } from './systems/monsters';
import { petSystem, spawnPet } from './systems/pets';
import { movementSystem } from './systems/movement';
import { collectAllPickups, pickupSystem } from './systems/pickups';
import { projectileSystem } from './systems/projectiles';
import { statusSystem } from './systems/status';
import { summonSystem } from './systems/summons';
import { effectiveDamage, spellSystem } from './systems/spells';
import { cardChanges, derivedStats, itemChanges, type DerivedChange, type DerivedStats } from './stats';
import { waveSystem } from './systems/waves';
import type { CoreEvent, Entity, EntityKind, InputFrame, Monster, Player, RunCommand } from './types';
import { adoptPlayer, bodyHeight, clampToWalkable, createWorld, entitiesOfKind, spawnPlayer, type World } from './world';

export const FIXED_DT = 1 / 60;

/** Sprites des poupées que la classe peut invoquer (sort principal et sorts proposés), sans doublon : à charger avec la salle. */
function summonSprites(content: Content, cls: Content['classes'][string]): string[] {
  const sprites = new Set<string>();
  for (const id of [cls.primarySpell, ...cls.spells]) {
    for (const fx of content.spells[id]?.effects ?? []) {
      const sprite = fx.type === 'summon' ? content.summons[fx.summonId]?.sprite : undefined;
      if (sprite) sprites.add(sprite);
    }
  }
  return [...sprites];
}

export type RunPhase = 'combat' | 'doors' | 'levelUp' | 'shop' | 'ended';
/** `startRoom` (0 à 5, vérifications et tests) : commence directement à cette salle de l'étage, 5 = boss. */
/**
 * `bonuses` : bonus de méta de la run (bornés à l'entrée) ; `clientId`, `dailySeed`, `dailyDay` et `dailySource` (jour et provenance de la
 * seed du jour), `talentsOwned` (compte des rangs) et `practice` (run d'entraînement : aucune progression enregistrée) sont repris tels
 * quels dans le résumé, sans effet sur la simulation.
 */
export interface RunOptions { seed: number; classId: string; floorId: string; startRoom?: number; bonuses?: MetaBonuses; clientId?: string; dailySeed?: boolean; dailyDay?: string; dailySource?: 'server' | 'local'; talentsOwned?: number; practice?: boolean; }
/** Durée (s) du bouclier posé à chaque entrée de salle par le bonus de méta. */
const ROOM_SHIELD_SECONDS = 8;
/** Pose le bouclier d'entrée de salle (bonus `shieldOnRoom`) ; sans bonus, ne touche à rien. */
export function applyRoomShield(world: World, p: Player): void {
  if (world.meta.shieldOnRoom > 0) p.shield = { amount: world.meta.shieldOnRoom, until: world.time + ROOM_SHIELD_SECONDS };
}
export interface SnapshotEntity { id: number; kind: EntityKind; sprite: string; x: number; y: number; facingX: number; facingY: number; anim: Entity['anim']; animTime: number; hp: number; hpMax: number; radius: number; height: number; alive: boolean; tint: 'slow' | 'poison' | null; telegraph: boolean; boss: boolean; }
export interface SnapshotSpell { id: string; name: string; icon: string; level: number; cooldown: number; cooldownMax: number; }
export interface SnapshotPrimary { id: string; name: string; icon: string; level: number; cooldownMax: number; damage: Damage | null; }
export interface Snapshot {
  time: number; entities: SnapshotEntity[];
  player: { hp: number; hpMax: number; level: number; xp: number; xpNext: number; kamas: number; attackCooldown: number; alive: boolean; spells: SnapshotSpell[]; equipment: Partial<Record<Slot, string>>; rerolls: number; stats: Stats; primary: SnapshotPrimary; classId: string; className: string; derived: DerivedStats } | null;
  phase: RunPhase; roomIndex: number; roomsTotal: number; roomId: string; wave: number; waves: number; cleared: boolean;
  /** `cardEffects[i]` et `shop.effects[i]` : effet de la carte ou de l'offre sur les stats dérivées (vide si aucun). */
  doors: DoorOffer[]; cards: LevelUpCard[]; cardEffects: DerivedChange[][]; shop: { offers: (ShopOffer | null)[]; rerollPrice: number; freeRerolls: number; effects: (DerivedChange[] | null)[] } | null;
  boss: { name: string; hp: number; hpMax: number } | null;
}
export interface RunSummary { seed: number; classId: string; floorId: string; outcome: 'victory' | 'died'; durationMs: number; kills: number; kamas: number; level: number; roomsCleared: number; refletsEarned: number; clientId: string; score: number; stats: RunStats; dailySeed: boolean; dailyDay: string | null; dailySource: 'server' | 'local' | null; talentsOwned: number; petId: string | null; practice: boolean; }
/** Actifs d'affichage d'une salle : image de fond (salle à rectangles), sous-zone (atlas de tuiles) et sprites (joueur, poupées, familier et monstres). */
export interface RoomAssets { map: string; biome?: string; sprites: string[]; }
/** `room()` : salle courante (avec sa grille pour une salle générée), pour le rendu ; `roomAssets()` / `nextRoomAssets()` : atlas à charger pour la salle courante / suivante (null après la dernière). */
export interface Run { step(input: InputFrame): CoreEvent[]; command(cmd: RunCommand): void; snapshot(): Snapshot; summary(): RunSummary; room(): RoomDef; roomAssets(): RoomAssets; nextRoomAssets(): RoomAssets | null; readonly done: boolean; readonly phase: RunPhase; }

const sumOf = (mods: readonly RoomModifierDef[], key: 'kamasPct' | 'xpPct' | 'refletsPct' | 'victoryRefletsPct' | 'monsterSpeedPct'): number => mods.reduce((a, m) => a + m[key], 0);

export function createRun(content: Content, opts: RunOptions): Run {
  const cls = content.classes[opts.classId];
  if (!cls) throw new Error(`Classe introuvable : ${opts.classId}`);
  const rng = createRng(opts.seed);
  const base = opts.bonuses ?? ZERO_META_BONUSES;
  // modificateurs de salle : leurs Kamas, XP et Reflets s'ajoutent aux bonus de méta avant tout tirage
  const mods = activeModifiers(content, base);
  const meta = clampBonuses(mergeBonuses(base, { kamasPct: sumOf(mods, 'kamasPct'), xpPct: sumOf(mods, 'xpPct'), refletsPct: sumOf(mods, 'refletsPct') }));
  const monsterSpeedPct = sumOf(mods, 'monsterSpeedPct');
  // Reflets de victoire (modificateur Gardien éveillé) : ne s'ajoutent qu'au résumé d'une victoire
  const victoryRefletsPct = sumOf(mods, 'victoryRefletsPct');
  const tracker = createStatsTracker();
  const floor: FloorState = startFloor(content, opts.floorId, rng);
  if (opts.startRoom) floor.roomIndex = Math.min(ROOMS_TOTAL - 1, Math.max(0, Math.floor(opts.startRoom)));
  let world: World = createWorld(content, roomSetup(floor, content, mods).room, rng, { meta, monsterSpeedPct });
  let player: Player | null = null;
  let phase: RunPhase = 'combat';
  let afterLevelUp: 'shop' | 'doors' | null = null;
  let pendingLevelUps = 0;
  let cards: LevelUpCard[] = [];
  let shop: ShopState | null = null;
  const pending: RunCommand[] = [];
  const carry: CoreEvent[] = [];
  let kills = 0, roomsCleared = 0;
  // portes sur les sorties : le passage ne s'arme qu'une fois le joueur sorti de toute zone de porte
  let doorsArmed = false;
  let outcome: RunSummary['outcome'] | null = null;

  function enterRoom(): void {
    const setup = roomSetup(floor, content, mods);
    const prev = world;
    world = createWorld(content, setup.room, rng, { waves: setup.waves, hpMult: setup.hpMult, meta, monsterSpeedPct });
    world.time = prev.time;
    world.events = prev.events; // les événements déjà émis pendant ce pas ne sont pas perdus
    if (!player) {
      player = spawnPlayer(world, cls!, setup.room.playerStart);
      for (const slot of meta.startItems) grantItemOfSlot(world, player, slot);
    } else adoptPlayer(world, player, setup.room.playerStart);
    // familier : recréé à chaque salle, à la gauche du joueur
    const petDef = meta.pet ? content.pets[meta.pet] : undefined;
    if (petDef) spawnPet(world, petDef, clampToWalkable({ x: player.pos.x - petDef.followDistance, y: player.pos.y }, petDef.radius, world.room));
    applyRoomShield(world, player);
    applyEntryReward(world, player, floor);
    world.events.push({ type: 'roomEntered', roomIndex: floor.roomIndex, roomId: setup.room.id, reward: floor.reward });
    tracker.onRoomEntered(isBossRoom(floor), floor.reward, world.time);
    phase = 'combat';
  }
  // atlas du familier (recréé à chaque salle) : chargé avec chaque salle comme les poupées
  const petSprites = meta.pet && content.pets[meta.pet] ? [content.pets[meta.pet]!.sprite] : [];
  function assetsOf(index: number): RoomAssets | null {
    if (index >= ROOMS_TOTAL) return null;
    const a = floorRoomAssets(floor, content, index);
    return { map: a.room.map, ...(a.room.grid?.biome ? { biome: a.room.grid.biome } : {}), sprites: [cls!.sprite, ...summonSprites(content, cls!), ...petSprites, ...a.sprites] };
  }
  function toDoors(): void {
    phase = 'doors';
    offerDoors(floor, rng);
    // salle générée : chaque porte est posée sur une sortie ; salle à rectangles : choix au panneau seulement
    const at = placeDoors(world.room, floor.doors.length);
    if (at) floor.doors.forEach((d, i) => { d.pos = at[i]; });
    doorsArmed = false;
    world.events.push({ type: 'doorsOffered', count: floor.doors.length });
  }
  /** Phase des portes : le joueur marche (temps de combat figé, plus aucun monstre) ; une porte touchée est choisie. */
  function doorStep(input: InputFrame): void {
    const p = player!;
    movementSystem(world, input, FIXED_DT);
    animSystem(world, FIXED_DT);
    const i = doorAt(floor.doors, p.pos);
    if (i < 0) { doorsArmed = true; return; }
    if (doorsArmed) handle({ type: 'chooseDoor', index: i });
  }
  function toShop(): void { shop = openShop(world, player!, floor.roomIndex); phase = 'shop'; }
  function toLevelUp(): void { cards = drawCards(world, player!); phase = 'levelUp'; }
  function end(o: RunSummary['outcome']): void { outcome = o; phase = 'ended'; world.events.push({ type: 'runEnded', outcome: o }); }
  function invalid(reason: string): void { world.events.push({ type: 'invalidCommand', reason }); }

  function handle(cmd: RunCommand): void {
    const p = player!;
    if (cmd.type === 'chooseDoor') {
      if (phase !== 'doors') return invalid('chooseDoor hors phase doors');
      if (!chooseDoor(floor, cmd.index)) return invalid('porte inexistante');
      enterRoom();
    } else if (cmd.type === 'chooseCard') {
      if (phase !== 'levelUp') return invalid('chooseCard hors phase levelUp');
      const card = cards[cmd.index];
      if (!card) return invalid('carte inexistante');
      applyCard(world, p, card);
      pendingLevelUps = Math.max(0, pendingLevelUps - 1);
      if (pendingLevelUps > 0) toLevelUp();
      else { const next = afterLevelUp; afterLevelUp = null; if (next === 'shop') toShop(); else if (next === 'doors') toDoors(); else phase = 'combat'; }
    } else if (cmd.type === 'rerollCards') {
      if (phase !== 'levelUp') return invalid('rerollCards hors phase levelUp');
      if (p.rerolls <= 0) return invalid('plus de relance');
      p.rerolls -= 1; tracker.stats.rerollsUsed++; toLevelUp();
    } else if (cmd.type === 'buy') {
      if (phase !== 'shop' || !shop) return invalid('buy hors phase shop');
      if (!buy(world, p, shop, cmd.index)) invalid('achat impossible');
    } else if (cmd.type === 'rerollShop') {
      if (phase !== 'shop' || !shop) return invalid('rerollShop hors phase shop');
      if (!rerollShop(world, p, shop)) invalid('relance impossible');
    } else {
      if (phase !== 'shop') return invalid('leaveShop hors phase shop');
      shop = null; toDoors();
    }
  }

  enterRoom();
  carry.push(...world.events);

  /** Un pas de simulation ; les compteurs de run observent ses événements après coup, sans l'influencer. */
  function simulate(input: InputFrame): CoreEvent[] {
    const wasPhase = phase;
    for (const cmd of pending.splice(0)) handle(cmd);
    // Portes : on marche jusqu'à une sortie (ou on choisit au panneau, commande traitée ci-dessus).
    if (wasPhase === 'doors' && phase === 'doors') { doorStep(input); return world.events; }
    // Un pas qui sort d'un panneau (choix de carte, de porte...) ne simule pas : la touche du choix ne lance pas de sort.
    if (wasPhase !== 'combat' || phase !== 'combat') return world.events;
    const p = player!;
    movementSystem(world, input, FIXED_DT);
    spellSystem(world, input, FIXED_DT);
    projectileSystem(world, FIXED_DT);
    summonSystem(world, FIXED_DT);
    monsterSystem(world, FIXED_DT);
    petSystem(world, FIXED_DT);
    statusSystem(world);
    pickupSystem(world);
    waveSystem(world);
    const cleared = world.events.some((e) => e.type === 'roomCleared');
    if (cleared) collectAllPickups(world);
    animSystem(world, FIXED_DT);
    world.time += FIXED_DT;
    for (const e of world.events) {
      // les poupées (invocations) ne comptent pas comme des monstres tués
      if (e.type === 'death' && e.defId !== opts.classId && !content.summons[e.defId]) {
        kills++;
        // rembobinage : chaque monstre tué raccourcit toutes les recharges en cours
        if (meta.rewindCooldown > 0) for (const k of Object.keys(p.cooldowns)) p.cooldowns[k] = Math.max(0, (p.cooldowns[k] ?? 0) - meta.rewindCooldown);
      }
    }
    if (!p.alive) { end('died'); return world.events; }
    if (cleared) {
      roomsCleared++;
      const { openShop: wantShop } = applyClearReward(world, p, floor);
      // Tous les level-ups de ce pas (combat et récompense d'XP) sont dans world.events.
      pendingLevelUps = world.events.filter((e) => e.type === 'levelUp').length;
      if (isBossRoom(floor)) { end('victory'); return world.events; }
      const next: 'shop' | 'doors' = wantShop ? 'shop' : 'doors';
      if (pendingLevelUps > 0) { afterLevelUp = next; toLevelUp(); }
      else if (next === 'shop') toShop(); else toDoors();
    } else {
      pendingLevelUps = world.events.filter((e) => e.type === 'levelUp').length;
      if (pendingLevelUps > 0) toLevelUp();
    }
    return world.events;
  }

  const run: Run = {
    get done() { return outcome !== null; },
    get phase() { return phase; },
    command(cmd) { if (outcome === null) pending.push(cmd); },
    room() { return world.room; },
    roomAssets() { return assetsOf(floor.roomIndex)!; },
    nextRoomAssets() { return assetsOf(floor.roomIndex + 1); },
    step(input) {
      world.events = carry.splice(0);
      if (outcome !== null) return [];
      const events = simulate(input);
      tracker.onEvents(events, world, player!, player!.id);
      if (events.some((e) => e.type === 'roomCleared')) tracker.onRoomCleared(world.time);
      return events;
    },
    snapshot() {
      const p = player!;
      const primarySpell = content.spells[p.primary.id]!;
      const boss = entitiesOfKind<Monster>(world, 'monster').find((m) => m.boss && m.alive) ?? null;
      return {
        time: world.time,
        entities: [...world.entities.values()].map((e) => ({ id: e.id, kind: e.kind, sprite: e.sprite, x: e.pos.x, y: e.pos.y, facingX: e.facing.x, facingY: e.facing.y, anim: e.anim, animTime: e.animTime, hp: e.hp, hpMax: e.hpMax, radius: e.radius, height: bodyHeight(e as { radius: number; height?: number }), alive: e.alive, tint: e.alive && e.kind === 'monster' ? (e.slow ? 'slow' as const : e.poison ? 'poison' as const : null) : null, telegraph: e.alive && e.kind === 'monster' && (e.windup > 0 || e.charge?.phase === 'windup'), boss: e.kind === 'monster' && e.boss })),
        player: { hp: p.hp, hpMax: p.hpMax, level: p.level, xp: p.xp, xpNext: p.xpNext, kamas: p.kamas, attackCooldown: p.attackCooldown, alive: p.alive, rerolls: p.rerolls, equipment: { ...p.equipment }, stats: { ...p.stats }, classId: p.defId, className: content.classes[p.defId]!.name, derived: derivedStats(p, content, playerDamagePct(world, p), meta),
          primary: { id: p.primary.id, name: primarySpell.name, icon: primarySpell.icon, level: p.primary.level, cooldownMax: effectiveCooldown(primarySpell, p.primary, meta.cooldownPct), damage: effectiveDamage(primarySpell, p.primary, p.stats, playerDamagePct(world, p), meta.damagePct) },
          spells: p.spells.map((o) => { const s = content.spells[o.id]!; return { id: o.id, name: s.name, icon: s.icon, level: o.level, cooldown: p.cooldowns[o.id] ?? 0, cooldownMax: effectiveCooldown(s, o, meta.cooldownPct) }; }) },
        phase, roomIndex: floor.roomIndex, roomsTotal: ROOMS_TOTAL, roomId: world.room.id, wave: Math.min(world.wave + 1, Math.max(1, world.waves.length)), waves: world.waves.length, cleared: world.cleared,
        doors: phase === 'doors' ? floor.doors.map((d) => ({ ...d, pos: d.pos ? { ...d.pos } : undefined })) : [], cards: phase === 'levelUp' ? [...cards] : [], cardEffects: phase === 'levelUp' ? cards.map((c) => cardChanges(content, p, c, meta)) : [],
        shop: phase === 'shop' && shop ? { offers: shop.offers.map((o) => (o ? { ...o } : null)), rerollPrice: shop.rerollPrice, freeRerolls: shop.freeRerolls, effects: shop.offers.map((o) => (o ? itemChanges(content, p, o.item, meta) : null)) } : null,
        boss: boss ? { name: content.monsters[boss.defId]?.name ?? boss.defId, hp: boss.hp, hpMax: boss.hpMax } : null,
      };
    },
    summary() {
      const p = player!;
      const out = outcome ?? 'died';
      const durationMs = Math.round(world.time * 1000);
      const stats = structuredClone(tracker.stats);
      const refletsPct = Math.min(300, meta.refletsPct + (out === 'victory' ? victoryRefletsPct : 0));
      return {
        seed: opts.seed, classId: opts.classId, floorId: opts.floorId, outcome: out, durationMs, kills, kamas: p.kamas, level: p.level, roomsCleared,
        refletsEarned: Math.round((kills * 2 + roomsCleared * 15 + (out === 'victory' ? 100 : 0)) * (1 + refletsPct / 100)),
        clientId: opts.clientId ?? `seed-${opts.seed}`, score: scoreOf({ kills, roomsCleared, level: p.level, outcome: out, durationMs, stats }), stats,
        dailySeed: !!opts.dailySeed, dailyDay: opts.dailyDay ?? null, dailySource: opts.dailySource ?? null, talentsOwned: opts.talentsOwned ?? 0, petId: meta.pet, practice: !!opts.practice,
      };
    },
  };
  return run;
}
